import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MessageSquare, MoreHorizontal, Trash2, Upload, X } from 'lucide-react'
import { isContextMaterialIndexingReady } from '@digital-twins/shared-types'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { KnowledgeDocumentWithStages } from '@/lib/api'
import { formatLibraryDate } from '../KnowledgeHubPage/libraryHelpers'
import { KnowledgeIndexingBadge } from '../KnowledgeHubPage/KnowledgeIndexingBadge'
import {
  knowledgeStageFailures,
  KnowledgeStageFailureNotice,
} from '../KnowledgeHubPage/KnowledgeStageFailureNotice'
import { DetailsPanel } from './DetailsPanel'
import { PreviewPane } from './PreviewPane'
import { useKnowledgeDocumentDetail } from './useKnowledgeDocumentDetail'

export interface KnowledgeDocumentModalProps {
  documentToken: string
  onClose: () => void
  onDeleted?: (documentToken: string) => void
  onDocumentUpdated?: (doc: KnowledgeDocumentWithStages) => void
}

// Matches the transition duration below — the pane unmounts only after its
// exit animation finishes, not the instant a close is requested.
const CLOSE_ANIMATION_MS = 180

/**
 * Wide overlay pane for the library's document preview, opened on top of
 * KnowledgeHubPage instead of a full-page navigation. Renders the same
 * PreviewPane + DetailsPanel body as KnowledgeDocumentPage via the shared
 * useKnowledgeDocumentDetail hook, styled after AudienceDetailModal.
 */
export function KnowledgeDocumentModal({
  documentToken,
  onClose,
  onDeleted,
  onDocumentUpdated,
}: KnowledgeDocumentModalProps) {
  const { t } = useTranslation('app')
  const navigate = useNavigate()

  const {
    doc,
    setDoc,
    loading,
    title,
    versions,
    downloading,
    deleting,
    facetBusy,
    savingDetails,
    instructionsDraft,
    setInstructionsDraft,
    instructionsBusy,
    replacing,
    replaceInputRef,
    pagesLoading,
    preview,
    pageUnknown,
    availablePages,
    maxKnownPage,
    selectPage,
    handleDownload,
    handleDelete,
    handleSaveDetails,
    handleConfirmFacet,
    handleCorrectFacet,
    handleRejectFacet,
    handleConfirmAllFacets,
    handleSaveInstructions,
    handleReplaceFile,
  } = useKnowledgeDocumentDetail({
    documentToken,
    onDeleted: () => {
      onDeleted?.(documentToken)
      handleClose()
    },
  })

  const [visible, setVisible] = useState(false)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  function handleClose() {
    if (closing) return
    setClosing(true)
    window.setTimeout(onClose, CLOSE_ANIMATION_MS)
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleDocumentUpdated(updated: KnowledgeDocumentWithStages) {
    setDoc(updated)
    onDocumentUpdated?.(updated)
  }

  const shown = visible && !closing

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end overflow-y-auto p-4"
      data-testid="knowledge-doc-modal"
    >
      <div
        className={cn(
          'fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-[180ms] ease-out',
          shown ? 'opacity-100' : 'opacity-0',
        )}
        onClick={handleClose}
      />
      <div
        className={cn(
          'relative z-10 flex h-full w-full max-w-[1302px] flex-col overflow-hidden rounded-2xl bg-card-inset shadow-large transition-all duration-[180ms] ease-out',
          shown ? 'translate-x-0 opacity-100' : 'translate-x-6 opacity-0',
        )}
      >
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2 md:px-5">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              data-testid="knowledge-doc-modal-breadcrumb"
            >
              {t('library.title')}
            </button>
            {doc &&
            (doc.isSimulationArtifact ||
              doc.isScannedPdf ||
              !isContextMaterialIndexingReady(doc.indexingStatus)) ? (
              <>
                <KnowledgeIndexingBadge status={doc.indexingStatus} />
                {doc.isSimulationArtifact ? (
                  <Badge variant="muted" data-testid="knowledge-doc-sim-badge">
                    {t('library.type.simulationReport')}
                  </Badge>
                ) : null}
                {doc.isScannedPdf ? (
                  <Badge variant="warning" data-testid="knowledge-doc-scanned-badge">
                    {t('library.noSearchableText')}
                  </Badge>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {doc ? (
              <>
                <input
                  ref={replaceInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.html,.htm,.json,.mp4,.mp3,.webm,.mov,.wav,.m4a,application/pdf,video/mp4,audio/mpeg,audio/mp3"
                  data-testid="knowledge-doc-modal-replace-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      void handleReplaceFile(file)
                    }
                  }}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      shape="pill"
                      className="h-8 w-8"
                      data-testid="knowledge-doc-modal-more"
                      aria-label={t('library.preview.moreActions')}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl">
                    <DropdownMenuItem
                      data-testid="knowledge-doc-modal-ask"
                      onClick={() => {
                        navigate(`/threads?doc=${encodeURIComponent(documentToken)}`)
                      }}
                    >
                      <MessageSquare className="mr-2 h-4 w-4" />
                      {t('library.preview.askAboutThis')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="knowledge-doc-modal-replace"
                      disabled={replacing}
                      onClick={() => replaceInputRef.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {replacing ? t('library.preview.replacing') : t('library.preview.replaceFile')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel data-testid="knowledge-doc-modal-versions">
                      {t('library.preview.versionHistory')}
                    </DropdownMenuLabel>
                    {versions.map((version) => (
                      <DropdownMenuItem
                        key={`${version.version}-${version.createdAt}`}
                        disabled
                        className="gap-2"
                        data-testid="knowledge-doc-modal-version-row"
                      >
                        <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium">
                          {version.version.replace(/^v?/i, 'v')}
                        </span>
                        <Text as="span" size="xs" color="muted">
                          {version.isCurrent ? t('library.preview.current') : version.label} ·{' '}
                          {formatLibraryDate(version.createdAt)}
                          {version.ownerName
                            ? ` ${t('library.preview.versionBy', { name: version.ownerName })}`
                            : doc.ownerName
                              ? ` ${t('library.preview.versionBy', { name: doc.ownerName })}`
                              : ''}
                        </Text>
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      data-testid="knowledge-doc-modal-delete"
                      disabled={deleting}
                      onClick={() => void handleDelete()}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('library.delete')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : null}
            <button
              type="button"
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              data-testid="knowledge-doc-modal-close"
              aria-label={t('library.preview.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="px-4 pt-3 md:px-6">
            <Skeleton className="h-40 rounded-2xl" />
          </div>
        ) : !doc ? (
          <div
            className="mx-4 mt-3 rounded-2xl border border-card-border bg-muted p-8 text-center md:mx-6"
            data-testid="knowledge-doc-modal-missing"
          >
            <Text color="muted">{t('library.preview.toastNotFound')}.</Text>
          </div>
        ) : (
          <div
            className="flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto"
            data-testid="knowledge-doc-modal-detail"
          >
            <div className="shrink-0 space-y-2 px-4 pt-3 md:px-6">
              <KnowledgeStageFailureNotice
                doc={doc}
                variant="detail"
                onDocumentUpdated={handleDocumentUpdated}
              />

              {doc.isScannedPdf ? (
                <Text size="sm" color="muted" data-testid="knowledge-doc-modal-scanned-banner">
                  This PDF has little extractable text, so it will not appear usefully in search.
                </Text>
              ) : null}
              {doc.indexingError && knowledgeStageFailures(doc).length === 0 ? (
                <Text size="sm" className="text-destructive">
                  {doc.indexingError}
                </Text>
              ) : null}
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 p-4 pt-3 md:gap-4 md:px-6 lg:grid-cols-3">
              <PreviewPane
                className="min-h-[24rem] lg:col-span-2"
                documentToken={documentToken}
                mimeType={doc.mimeType}
                originalFilename={doc.originalFilename}
                sourceUrl={doc.sourceUrl}
                linkThumbnailUrl={doc.linkThumbnailUrl}
                title={title}
                preview={preview}
                availablePages={availablePages}
                maxKnownPage={maxKnownPage}
                pagesLoading={pagesLoading}
                pageUnknown={pageUnknown}
                downloading={downloading}
                onSelectPage={(pageNumber) => void selectPage(pageNumber)}
                onDownload={() => void handleDownload()}
              />

              <DetailsPanel
                className="lg:col-span-1"
                document={doc}
                savingDetails={savingDetails}
                onSaveTitle={(next) => void handleSaveDetails({ title: next })}
                onSaveSummary={(next) => void handleSaveDetails({ summary: next })}
                facetBusy={facetBusy}
                onConfirmFacet={(facet) => void handleConfirmFacet(facet)}
                onRejectFacet={(facet) => void handleRejectFacet(facet)}
                onCorrectFacet={(facet, value) =>
                  void handleCorrectFacet(facet, value)
                }
                onConfirmAllFacets={() => void handleConfirmAllFacets()}
                instructionsDraft={instructionsDraft}
                instructionsBusy={instructionsBusy}
                onInstructionsChange={setInstructionsDraft}
                onSaveInstructions={() => void handleSaveInstructions()}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
